/**
 * Numbers of the "Hoy en Gula" panel on Inicio and the shipping feed. Pure
 * functions so they can be tested and stay honest.
 */
import type { GulaTask } from "../gulaTasks";
import { taskGroup } from "../gulaTasks";
import type { GulaClient, GulaOrder } from "../gulaTypes";
import { fullName } from "../gulaTypes";
import { isInSlot } from "../useAgents";

const DAY = 86_400_000;

export const startOfDay = (date: Date) => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
};

const isSameDay = (value: string | null | undefined, day: Date) =>
  !!value &&
  startOfDay(new Date(value)).getTime() === startOfDay(day).getTime();

const isThisMonth = (value: string | null | undefined, now: Date) => {
  if (!value) return false;
  const date = new Date(value);
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth()
  );
};

/** Lead stages that are still waiting for a call. */
export const CALLING_STAGES = [
  "new",
  "no_answer",
  "first_contact",
  "second_contact",
];

/** Has the lead already been called (or moved by a call) today? */
export const calledToday = (client: GulaClient, now: Date) =>
  isSameDay(client.last_call_attempt_at, now) ||
  (client.stage !== "new" && isSameDay(client.stage_changed_at, now));

const names = (records: { first_name?: string; last_name?: string }[]) =>
  records
    .slice(0, 2)
    .map((record) => fullName(record).split(" ")[0])
    .join(", ") + (records.length > 2 ? ` y ${records.length - 2} más` : "");

export interface HomeKpis {
  leadsToday: number;
  leadsYesterday: number;
  unowned: number;
  unownedNames: string;
  callNow: number;
  myTasksToday: number;
  myTasksOverdue: number;
  salesMonth: number;
  salesLastMonth: number;
  // Shipping and installation flow
  toShip: number;
  toShipLate: number;
  toShipNames: string;
  inTransit: number;
  inTransitWithTracking: number;
  outForDelivery: number;
  incidents: number;
  received: number;
  receivedWithoutParte: number;
  receivedNames: string;
  receivedToday: number;
  installerNotified: number;
  installerNotifiedToday: number;
  installationsDated: number;
  installedMonth: number;
}

export const homeKpis = ({
  clients,
  orders,
  tasks,
  meId,
  slot,
  now = new Date(),
}: {
  clients: GulaClient[];
  orders: GulaOrder[];
  tasks: GulaTask[];
  meId?: string | number | null;
  slot: string | null;
  now?: Date;
}): HomeKpis => {
  const yesterday = new Date(now.getTime() - DAY);
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 15);
  const active = orders.filter((order) => order.stage !== "cancelled");
  const unowned = clients.filter(
    (client) => client.stage === "new" && client.sales_id == null,
  );
  const myTasks = tasks.filter(
    (task) => meId != null && String(task.sales_id) === String(meId),
  );
  const toShip = active.filter((order) => order.stage === "to_ship");
  const inTransit = active.filter((order) => order.stage === "shipped");
  const received = active.filter((order) => order.stage === "received");
  const notified = active.filter(
    (order) => order.stage === "installer_notified",
  );
  // A sale counts the month the order was created (web or call)
  const sales = (month: Date) =>
    active.filter((order) => isThisMonth(order.created_at, month)).length;

  return {
    leadsToday: clients.filter((client) => isSameDay(client.created_at, now))
      .length,
    leadsYesterday: clients.filter((client) =>
      isSameDay(client.created_at, yesterday),
    ).length,
    unowned: unowned.length,
    unownedNames: names(unowned),
    callNow: slot
      ? clients.filter(
          (client) =>
            CALLING_STAGES.includes(client.stage) &&
            isInSlot(client.call_slot, slot) &&
            !calledToday(client, now),
        ).length
      : 0,
    myTasksToday: myTasks.filter(
      (task) => taskGroup(task.due_date, now) === "today",
    ).length,
    myTasksOverdue: myTasks.filter(
      (task) => taskGroup(task.due_date, now) === "overdue",
    ).length,
    salesMonth: sales(now),
    salesLastMonth: sales(lastMonth),
    toShip: toShip.length,
    toShipLate: toShip.filter(
      (order) =>
        now.getTime() - new Date(order.stage_changed_at).getTime() > 2 * DAY,
    ).length,
    toShipNames: names(toShip),
    inTransit: inTransit.length,
    inTransitWithTracking: inTransit.filter(
      (order) => !!order.tracking_emailed_at,
    ).length,
    outForDelivery: inTransit.filter(
      (order) => order.delivery_status === "out_for_delivery",
    ).length,
    incidents: inTransit.filter((order) =>
      ["incident", "returned"].includes(order.delivery_status ?? ""),
    ).length,
    received: received.length,
    receivedWithoutParte: received.filter(
      (order) => !order.installer_notified_at,
    ).length,
    receivedNames: names(received),
    receivedToday: active.filter((order) => isSameDay(order.received_at, now))
      .length,
    installerNotified: notified.length,
    installerNotifiedToday: active.filter((order) =>
      isSameDay(order.installer_notified_at, now),
    ).length,
    installationsDated: notified.filter((order) => !!order.installation_date)
      .length,
    installedMonth: active.filter((order) =>
      isThisMonth(order.installed_at, now),
    ).length,
  };
};

// ---------------------------------------------------------------------------
// Shipping feed: what happened to the orders lately
// ---------------------------------------------------------------------------

export type OrderEventKind =
  | "shipped"
  | "tracking"
  | "out_for_delivery"
  | "received"
  | "parte"
  | "installed"
  | "incident";

export interface OrderEvent {
  key: string;
  kind: OrderEventKind;
  at: string;
  order: GulaOrder;
}

const RECEIVED_BY: Record<string, string> = {
  packlink: "según el transportista",
  whatsapp: "confirmado por WhatsApp",
  phone: "confirmado por teléfono",
};

export const orderEvents = (orders: GulaOrder[], since: Date): OrderEvent[] => {
  const events: OrderEvent[] = [];
  const add = (order: GulaOrder, kind: OrderEventKind, at?: string | null) => {
    if (at && new Date(at) >= since)
      events.push({ key: `${order.id}-${kind}`, kind, at, order });
  };
  for (const order of orders) {
    if (order.stage === "cancelled") continue;
    add(order, "shipped", order.shipped_at);
    add(order, "tracking", order.tracking_emailed_at);
    if (order.stage === "shipped") {
      if (order.delivery_status === "out_for_delivery")
        add(order, "out_for_delivery", order.delivery_event_at);
      if (["incident", "returned"].includes(order.delivery_status ?? ""))
        add(order, "incident", order.delivery_event_at);
    }
    add(order, "received", order.received_at);
    add(order, "parte", order.installer_notified_at);
    add(order, "installed", order.installed_at);
  }
  return events.sort(
    (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
  );
};

/** One line for the feed: what happened and a detail. */
export const describeOrderEvent = (event: OrderEvent) => {
  const { order } = event;
  const name = fullName(order);
  switch (event.kind) {
    case "shipped":
      return {
        title: `Enviado a ${name}`,
        detail: [order.packlink_service, order.city]
          .filter(Boolean)
          .join(" · "),
      };
    case "tracking":
      return {
        title: `Seguimiento enviado a ${name}`,
        detail: "Ya puede seguir su paquete por correo",
      };
    case "out_for_delivery":
      return {
        title: `${name}: en reparto hoy`,
        detail: order.delivery_event_city ?? "",
      };
    case "incident":
      return {
        title: `${name}: incidencia en el envío`,
        detail: order.delivery_event ?? "Revisa el envío",
      };
    case "received":
      return {
        title: `${name} ha recibido el equipo`,
        detail: RECEIVED_BY[order.received_source ?? ""] ?? "",
      };
    case "parte":
      return {
        title: `Parte enviado${order.installer_name ? ` a ${order.installer_name}` : " al instalador"}`,
        detail: `Instalación de ${name}`,
      };
    case "installed":
      return { title: `${name}: instalado`, detail: order.city ?? "" };
  }
};

/** "hoy 10:32", "ayer 18:05", "lun 9:10" */
export const eventTime = (value: string, now = new Date()) => {
  const date = new Date(value);
  const time = date.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (isSameDay(value, now)) return `hoy ${time}`;
  if (isSameDay(value, new Date(now.getTime() - DAY))) return `ayer ${time}`;
  return `${date.toLocaleDateString("es-ES", { weekday: "short", day: "numeric" })} ${time}`;
};
